import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from './supabase'
import { fetchMyProfile } from './myProfile'
import { safeStorage } from './safeStorage'

// ── App mode: Basic / Advanced / Custom ─────────────────────────────────────
// Jason 2026-08-16: some people on Discord just want a tracker — add a show,
// mark episodes, see what's next — not the whole app. Others want everything.
// Rather than pick one, let people choose: Basic hides every "extra" feature
// from nav AND blocks direct-URL access to it; Advanced turns everything on;
// Custom lets you pick individually.
//
// What's core (never toggleable, in any mode): Home, Tonight, My List,
// Discover, Profile/Settings, Notifications, Import/Invite/Help, Stats,
// History, and Cinephile Cards (the collectible-card hook is the app's
// central identity — turning it off in Basic would leave Basic feeling like
// a generic tracker with no NextEp in it, Jason's explicit call). The episode-
// mark clapboard flash is core interaction feedback, not an "extra" — it's
// never gated by this system at all, same as Cards.
//
// Deliberately does NOT touch the celebration/cinematic queue's internals
// (CardReveal / AchievementUnlock / WrapCelebration) — that chain is marked
// don't-refactor elsewhere in the app. This only gates whether an event that
// feeds it gets DISPATCHED in the first place (see isFeatureEnabled call
// sites in AchievementUnlock's trigger paths), never how the queue itself
// sequences or renders.
// v17 (docs/v17-scope.md, 2026-09-15): this is the SHELVED set — features that stay
// in the code behind a toggle. For You, The Canon, Shelves, Buzz, the Reading Room,
// Challenges and Roll Credits were CUT (0–2 users ever): their routes redirect home
// and their doors are gone. Nothing was deleted; see the scope doc before re-adding.
export const FEATURES = [
  { key: 'cliffhanger',  label: 'Cliffhanger',        desc: 'The daily guess-the-show puzzle' },
  { key: 'faceoff',      label: 'Face-Off',           desc: 'The daily Higher-or-Lower — no TV knowledge needed' },
  { key: 'verdict',      label: 'Verdict',            desc: 'One show a day, five gut calls, scored by how close you land' },
  { key: 'people',       label: 'People & Social',    desc: 'Follow friends, watch together, see who’s watching what' },
  { key: 'aftershow',    label: 'The Aftershow',      desc: 'Spoiler-safe episode discussion rooms' },
  { key: 'wrapped',      label: 'Wrapped & Rewind',   desc: 'Your year in TV, year-round' },
]
const ALL_KEYS = FEATURES.map(f => f.key)

export const MODES = { BASIC: 'basic', ADVANCED: 'advanced', CUSTOM: 'custom' }
// Namespaced per user_id — audit 2026-08-16: a bare global key meant a shared/reused
// device (or an account switch without a full reload) could briefly render user B's
// mode/flags using user A's leftover localStorage value for the instant before the
// async get_my_profile() hydration below lands and corrects it. A per-uid key means
// a first-time-on-this-device user simply has no cached value yet (falls back to the
// safe ADVANCED default) instead of inheriting someone else's.
const keyFor = (base, uid) => uid ? `${base}_${uid}` : base
const STORAGE_KEY = 'nextep_app_mode'
const STORAGE_CUSTOM_KEY = 'nextep_custom_features'

function resolveEnabled(mode, custom) {
  // Phase 1 (2026-10-18): the simple tracker ships with ONE daily game so day one has a
  // habit loop. Face-Off needs no TV knowledge; the other two stay behind the gate.
  if (mode === MODES.BASIC) return new Set(['faceoff'])
  if (mode === MODES.CUSTOM) return new Set(Array.isArray(custom) ? custom.filter(k => ALL_KEYS.includes(k)) : [])
  return new Set(ALL_KEYS) // advanced (and the default, so nobody's existing experience changes)
}

// Basic by default for anyone who joined from this date on, and for every guest
// (Jason 2026-09-12: "make it just a tracker and enable the other features on a
// per-user basis"). Measured the day of the decision: 79 real accounts, ZERO had
// ever chosen Basic — the Welcome step pre-selected Advanced, so "Next" just
// confirmed the default. Everyone from before keeps Advanced; nothing changes for
// the 22 people already using the app.
const BASIC_DEFAULT_FROM = Date.parse('2026-09-12T00:00:00Z')
function defaultModeFor(user) {
  // `user` is null on the FIRST render of every boot (useAuth's state starts null and
  // resolves async, even though App.jsx blocks visible routes on `loading` — this hook's
  // own useState initializer still runs during that first, discarded render). Advanced
  // here used to mean "everyone gets a moment of every feature unlocked, including a
  // fresh anonymous guest, until the effect below corrects it" — the pre-launch
  // adversarial sweep (2026-09-17) caught /cliffhanger, /aftershow and /people firing
  // their data fetches during exactly that window, behind what looked like a closed
  // gate. Default conservative instead: nothing is lost for a real Advanced member,
  // who corrects up within one effect tick (see the local-cache fast path below).
  if (!user) return MODES.BASIC                          // unresolved — corrects below
  if (user.is_anonymous) return MODES.BASIC
  const t = Date.parse(user.created_at || '')
  return (t && t >= BASIC_DEFAULT_FROM) ? MODES.BASIC : MODES.ADVANCED
}

export function useFeatureFlags(user) {
  const uid = user?.id || null
  const [mode, setModeState] = useState(() => {
    try { return safeStorage.get(keyFor(STORAGE_KEY, uid)) || defaultModeFor(user) } catch { return defaultModeFor(user) }
  })
  const [custom, setCustomState] = useState(() => {
    try { return JSON.parse(safeStorage.get(keyFor(STORAGE_CUSTOM_KEY, uid)) || '[]') } catch { return [] }
  })
  const hydratedFor = useRef(null)

  const persistServer = useCallback((nextMode, nextCustom) => {
    if (!uid) return
    supabase.rpc('get_my_profile').maybeSingle()
      .then(({ data }) => {
        const merged = { ...(data?.ui_prefs || {}), app_mode: nextMode, custom_features: nextCustom }
        supabase.from('profiles').update({ ui_prefs: merged }).eq('id', uid).then(() => {}, () => {})
      }, () => {})
  }, [uid])

  useEffect(() => {
    if (!uid || hydratedFor.current === uid) return
    hydratedFor.current = uid
    // Fast, synchronous path: the initial useState ran with uid=null (see
    // defaultModeFor's comment), so a RETURNING user's own cached preference under
    // their real uid was invisible to it. Apply it now, before the network round-trip,
    // so a real Advanced member corrects in the next tick instead of waiting on
    // get_my_profile.
    try {
      const cachedMode = safeStorage.get(keyFor(STORAGE_KEY, uid))
      if (cachedMode) setModeState(cachedMode)
    } catch {}
    fetchMyProfile(uid)
      .then(({ data }) => {
        const serverMode = data?.ui_prefs?.app_mode
        const serverCustom = data?.ui_prefs?.custom_features
        if (serverMode) {
          setModeState(serverMode)
          safeStorage.set(keyFor(STORAGE_KEY, uid), serverMode)
          if (Array.isArray(serverCustom)) {
            setCustomState(serverCustom)
            safeStorage.set(keyFor(STORAGE_CUSTOM_KEY, uid), JSON.stringify(serverCustom))
          }
        } else {
          // Nothing saved server-side yet — resolve the default for THIS user (the
          // initial state may have been computed before `user` existed, i.e. with the
          // pre-2026-09-12 Advanced fallback) and make it durable.
          const d = defaultModeFor(user)
          setModeState(d)
          safeStorage.set(keyFor(STORAGE_KEY, uid), d)
          persistServer(d, custom)
        }
      }, () => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, persistServer])

  const setMode = (next) => {
    setModeState(next)
    safeStorage.set(keyFor(STORAGE_KEY, uid), next)
    persistServer(next, custom)
  }

  const setCustomFeatures = (keys) => {
    const arr = keys.filter(k => ALL_KEYS.includes(k))
    setCustomState(arr)
    safeStorage.set(keyFor(STORAGE_CUSTOM_KEY, uid), JSON.stringify(arr))
    persistServer(mode, arr)
  }

  const toggleCustomFeature = (key) => {
    const next = custom.includes(key) ? custom.filter(k => k !== key) : [...custom, key]
    setCustomFeatures(next)
  }

  // Per-feature unlock from wherever the feature would naturally appear (FeatureGate's
  // "Turn on X" card, Home's games nudge) — Basic becomes Custom with just that key;
  // Custom gains the key; Advanced already has everything. One tap, nothing else changes.
  const unlockFeature = (key) => {
    if (!ALL_KEYS.includes(key)) return
    if (mode === MODES.ADVANCED) return
    const base = mode === MODES.CUSTOM ? custom : []
    const nextCustom = base.includes(key) ? base : [...base, key]
    setModeState(MODES.CUSTOM)
    safeStorage.set(keyFor(STORAGE_KEY, uid), MODES.CUSTOM)
    setCustomState(nextCustom)
    safeStorage.set(keyFor(STORAGE_CUSTOM_KEY, uid), JSON.stringify(nextCustom))
    persistServer(MODES.CUSTOM, nextCustom)
  }

  const enabled = resolveEnabled(mode, custom)
  const isEnabled = (key) => !key || enabled.has(key)

  // A few trigger sites (Achievements' unlock dispatch) live far from any
  // component that could receive this via props — same window-flag idiom
  // already used app-wide (__nextepOnboarding, __nextepModalHold, etc.)
  // rather than prop-threading through a provider tree that predates this.
  useEffect(() => { try { window.__nextepEnabledFeatures = enabled } catch {} }, [enabled])

  return { mode, setMode, custom, setCustomFeatures, toggleCustomFeature, unlockFeature, enabled, isEnabled }
}

// Maps a nav `to` path (BottomNav.jsx) or a route pathname prefix (App.jsx's
// route guard) to the feature key that gates it. Paths not listed here are
// core and always visible/reachable, regardless of mode.
export const PATH_FEATURE = {
  '/cliffhanger':  'cliffhanger',
  '/faceoff':      'faceoff',
  '/verdict':      'verdict',
  '/aftershow':    'aftershow',
  '/people':       'people',
  '/wrapped':      'wrapped',
  '/rewind':       'wrapped',
}

export function featureKeyForPath(pathname) {
  const hit = Object.keys(PATH_FEATURE).find(p => pathname === p || pathname.startsWith(p + '/'))
  return hit ? PATH_FEATURE[hit] : null
}
